import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Select from '../UI/Select';
import InputCurrency from '../UI/InputCurrency';
import InputTextarea from '../UI/InputTextarea';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { toast } from 'react-toastify';
import { createVoucher } from '@/api/voucher';
import { useUIStore } from '@/store/useUIStore';
import { TableStore } from '@/pages/Admin/Stores';
import axios from 'axios';
import { SelectOption } from '@/types/shared';

interface VoucherValues {
   code: string;
   price: string;
   store: string;
}

type ErrorState = {
   code: string;
   price: boolean;
   store: boolean;
};

interface RateLimitError {
   status: number;
   message: string;
   retryAfter?: number;
   err: string;
   msg: string;
}

const CreateVoucher = ({ data }: { data: TableStore[] }) => {
   const { handleClose, handleRefresh } = useUIStore();
   const [isLoading, setIsLoading] = useState(false);
   const [values, setValues] = useState<VoucherValues>({
      code: '',
      price: '',
      store: '',
   });

   const [storeData, setStoreData] = useState<SelectOption[]>([]);
   const [voucherCodes, setVoucherCodes] = useState<string[]>([]);

   const { price, store, code } = values;

   const [errors, setErrors] = useState<ErrorState>({
      code: '',
      price: false,
      store: false,
   });

   useEffect(() => {
      const processedCodes = code
         .split(/[\s\n]+/)
         .filter((c) => c.trim() !== '')
         .map((c) => c.replace(/[^a-zA-Z0-9]/g, ''))
         .filter((c) => c.length > 0);
      setVoucherCodes(processedCodes);
   }, [code]);

   useEffect(() => {
      const options = data.map((item) => ({
         _id: item._id,
         name: item.storename,
         srcIcon: '',
      }));
      setValues({
         code: '',
         price: '',
         store: options[0]?._id || '',
      });
      setErrors({ code: '', price: false, store: false });
      setStoreData(options);
      setVoucherCodes([]);
   }, [data]);

   const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
   ) => {
      const { name, value } = e.target;

      if (name === 'code') {
         const sanitized = value.replace(/[^a-zA-Z0-9\s\n]/g, '');
         setValues((prev) => ({ ...prev, [name]: sanitized }));
         const codes = sanitized
            .split(/[\s\n]+/)
            .filter((c) => c.trim() !== '' && c.length > 0);
         setVoucherCodes(codes);
         if (sanitized.trim() !== '') {
            setErrors((prev) => ({ ...prev, code: '' }));
         }
      } else {
         setValues((prev) => ({ ...prev, [name]: value }));
         if (value.trim() !== '') {
            setErrors((prev) => ({ ...prev, [name]: false }));
         }
      }
   };

   const validateForm = () => {
      const newErrors: ErrorState = {
         code: !code.trim()
            ? 'Voucher code is required!'
            : voucherCodes.length > 500
            ? 'Voucher codes limit is 500'
            : '',
         price: !price,
         store: !store,
      };
      setErrors(newErrors);
      return newErrors.code === '' && !newErrors.price && !newErrors.store;
   };

   const handleSubmit = async () => {
      if (isLoading) return;
      if (!validateForm()) {
         if (errors.code === 'Voucher codes limit is 500') {
            toast.error('Maximum 500 voucher codes allowed');
         }
         return;
      }
      try {
         setIsLoading(true);
         const response = await createVoucher({
            code: values.code,
            price: Number(values.price),
            store: values.store,
            _id: '',
            storeId: values.store,
            duration: '',
            sold: false,
         } as any);

         toast.success(response.msg, { autoClose: 5000 });
         handleRefresh();
         handleClose();
      } catch (err: unknown) {
         if (
            typeof err === 'object' &&
            err !== null &&
            'status' in err &&
            err.status === 429
         ) {
            const e = err as RateLimitError;
            const retryAfter = e.retryAfter || 60;
            toast.warning(
               `Too many actions at once. Try again in ${retryAfter}s.`
            );
         } else if (axios.isAxiosError(err)) {
            toast.error(
               err.response?.data?.msg || 'Failed to create vouchers'
            );
         } else {
            toast.error('Failed to create vouchers');
         }
      } finally {
         setIsLoading(false);
      }
   };

   const handleSelect = (item: SelectOption) => {
      setValues({ ...values, store: item._id });
      setErrors((prev) => ({ ...prev, store: false }));
   };

   return (
      <Wrapper>
         <Content>
            <Form>
               <Select
                  title="Select Store"
                  data={storeData}
                  onChange={handleSelect}
                  srcIconDefault="store-3-line"
                  error={errors.store}
               />
               <InputCurrency
                  label="Amount"
                  name="price"
                  value={price}
                  placeholder="0.00"
                  onChange={handleChange}
                  error={errors.price}
               />
               <Hint>
                  Title and description for this price tier can be edited
                  anytime from the <strong>Rates</strong> panel on the voucher
                  list.
               </Hint>

               <VoucherInputWrapper>
                  <VoucherLimitDisplay>
                     <VoucherLimitDisplayCount
                        $isError={voucherCodes.length > 500}
                     >
                        {voucherCodes.length}
                     </VoucherLimitDisplayCount>
                     <VoucherLimitDisplayMax>/500</VoucherLimitDisplayMax>
                  </VoucherLimitDisplay>

                  <InputTextarea
                     label="Enter voucher codes"
                     name="code"
                     value={code}
                     placeholder="e.g SMF000001 SMF000002 SMF000003"
                     onChange={handleChange}
                     error={errors.code}
                  />
               </VoucherInputWrapper>
            </Form>
            <BtnWrapper>
               <div onClick={handleClose}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
               <div onClick={handleSubmit}>
                  <Btn
                     title={isLoading ? 'Please wait...' : 'Add'}
                     type={isLoading ? 'disabled' : 'normal'}
                     size="small"
                     bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                     textcolor={isLoading ? colors.GRAY1 : colors.WHITE}
                     hasIcon={isLoading}
                     icon="loader"
                     iconPosition="right"
                  />
               </div>
            </BtnWrapper>
         </Content>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 500px;
   height: 100%;
   @media (max-width: 768px) { width: 100%; }
`;
const Form = styled.div`
   width: 100%;
   display: flex;
   flex-direction: column;
   gap: 1rem;
`;
const Content = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
`;
const Hint = styled.p`
   font-size: 12px;
   color: ${colors.GRAY4};
   line-height: 1.5;
   strong { color: ${colors.PRIMARY}; }
`;
const BtnWrapper = styled.div`
   width: 100%;
   display: flex;
   justify-content: flex-end;
   gap: 1rem;
   margin-top: 1rem;
   @media (max-width: 768px) { padding-bottom: 5rem; }
`;
const VoucherInputWrapper = styled.div` position: relative; `;
const VoucherLimitDisplay = styled.div`
   position: absolute;
   top: 0;
   right: 0;
   display: flex;
`;
const VoucherLimitDisplayCount = styled.div<{ $isError: boolean }>`
   font-size: 13px;
   color: ${({ $isError }) => ($isError ? colors.DANGER : colors.GRAY1)};
   font-weight: ${({ $isError }) => ($isError ? 'bold' : 'normal')};
`;
const VoucherLimitDisplayMax = styled.div`
   font-size: 13px;
   color: ${colors.GRAY1};
`;

export default CreateVoucher;
